/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { Fragment, useState, useRef, useEffect } from "react"
import { SectionGrid, Grid, Flex } from "frontend/src/components/Layout"
import Carousel from "nuka-carousel"
import { FiChevronLeft, FiChevronRight, FiShoppingCart } from "react-icons/fi"
import Img from "gatsby-image"
import { useCart } from "frontend/src/utils/useCart"
import { displayCent } from "frontend/src/utils/displayCent"
import { getFluidFromAsset } from "frontend/src/utils/gatsbyImageHelpers"
import { Icon } from "frontend/src/components/Icon"
import { Touchable } from "frontend/src/components/Touchable"

const buttonStyles = {
  cursor: "pointer",
  background: "#FFFFFF",
  border: "none",
  width: "55px",
  height: "55px",
  py: 4,
  px: 4,
  borderRadius: "50%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  position: "relative",
  bottom: ["100px", "100px", "106px"],
  "&:active": {
    outline: "none",
  },
  "&:focus": {
    outline: "none",
  },
}

const RiIcon = ({ Component, size, color }) => (
  <Fragment>
    <span sx={{ display: ["inline", "none", "none"] }}>
      <Component color={color} size={size[0]} />
    </span>
    <span sx={{ display: ["none", "inline", "none"] }}>
      <Component color={color} size={size[1]} />
    </span>
    <span sx={{ display: ["none", "none", "inline"] }}>
      <Component color={color} size={size[2]} />
    </span>
  </Fragment>
)

function Tag({ tag }) {
  const color = "almostBlack"
  if (typeof tag === "string" && tag.length > 0) {
    return (
      <div
        sx={{
          backgroundColor: color,
          color: "background",
          fontWeight: "regular",
          fontSize: "12px",
          ml: 0,
          textAlign: "left",
          px: 3,
          lineHeight: 1.0,
          py: 2,
          mb: 1,
        }}
      >
        {tag}
      </div>
    )
  }
  return null
}

function ProductImage({ image, tags = [], background = undefined, page }) {
  const containerRef = useRef(null)
  const [height, setHeight] = useState("300px")
  useEffect(() => {
    try {
      const adjustImages = () => {
        if (containerRef && containerRef.current) {
          const myHeight = `${containerRef.current.offsetWidth}px`
          setHeight(myHeight)
        }
      }
      if (window) {
        window.addEventListener("load", adjustImages)
        window.addEventListener("resize", adjustImages)
      }
      adjustImages()
    } catch (err) {
      console.error(err)
    }
  }, [])
  if (page) {
    return (
      <Touchable
        sx={{
          width: "100%",
          height: height,
          mt: 6,
          mb: 5,
          display: "grid",
          gridTemplateColumns: "1fr",
          gridTemplateRows: "1fr",
          position: "relative",
          backgroundColor: background ? background : "unset",
        }}
        to={`/${page.slug.current}`}
        ref={containerRef}
      >
        <Img
          fluid={getFluidFromAsset(image, 600)}
          sx={{ width: "100%", height: "100%", gridColumn: "1 / span 1", gridRow: "1 / span 1" }}
          imgStyle={{ objectFit: "contain" }}
        />
        <div
          className="tags-container"
          sx={{
            gridColumn: "1 / span 1",
            gridRow: "1 / span 1",
            position: "absolute",
            left: 0,
            top: ["10px", "10px", 5],
          }}
        >
          {tags.length > 0 && tags.map((tag, index) => <Tag key={index} tag={tag} />)}
        </div>
      </Touchable>
    )
  } else {
    return (
      <div
        sx={{
          width: "100%",
          height: height,
          mt: 6,
          mb: 5,
          display: "grid",
          gridTemplateColumns: "1fr",
          gridTemplateRows: "1fr",
          position: "relative",
          backgroundColor: background ? background : "unset",
        }}
        ref={containerRef}
      >
        <Img
          fluid={getFluidFromAsset(image, 600)}
          sx={{ width: "100%", height: "100%", gridColumn: "1 / span 1", gridRow: "1 / span 1" }}
          imgStyle={{ objectFit: "contain" }}
        />
        <div
          className="tags-container"
          sx={{
            gridColumn: "1 / span 1",
            gridRow: "1 / span 1",
            position: "absolute",
            left: 0,
            top: ["10px", "10px", 5],
          }}
        >
          {tags.length > 0 && tags.map((tag, index) => <Tag key={index} tag={tag} />)}
        </div>
      </div>
    )
  }
}

function ProductCard({ product, backgroundImage }) {
  const cart = useCart()
  const {
    productName,
    productDescription,
    singleProduct,
    singleVariant,
    productImage,
    productPage,
  } = product
  const { reviewRating, reviewCount, variants, tags, ourID: productId } = singleProduct
  const ourVariant = variants.find(variant => variant.ourID === singleVariant)
  const { price, comparePrice } = ourVariant
  return (
    <Flex.column
      align="center"
      sx={{
        pb: 5,
        mx: ["auto", "auto", "unset"],
        maxWidth: ["300px", "300px", "unset"],
        minWidth: ["300px", "210px", "unset"],
        width: "100%",
      }}
    >
      <ProductImage
        image={productImage}
        tags={tags}
        page={productPage}
        background={backgroundImage}
      />
      <div sx={{ color: "almostBlack" }}>
        <Flex.row justify="center" sx={{ ml: 2, mr: 3, mb: 2 }}>
          {[1, 2, 3, 4, 5].map((num, i) => (
            <Icon
              key={i}
              name="Star"
              size={["14px", "14px", "16px"]}
              color={num <= reviewRating ? "gold" : "#ddd"}
            />
          ))}
        </Flex.row>
        <div
          sx={{
            textAlign: "center",
            mt: 0,
            fontFamily: "body",
            fontWeight: "regular",
            fontSize: "12px",
            lineHeight: 1.0,
          }}
        >
          ({reviewCount || 0}{" "}
          {reviewCount > 1 ? "Bewertungen" : reviewCount === 1 ? "Bewertung" : "Bewertungen"})
        </div>
        <h3
          sx={{
            fontFamily: "heading",
            fontSize: ["16px", "16px", "20px"],
            lineHeight: [1.0, 1.14, 1.1],
            letterSpacing: ["-1px", "-0.67px", "-0.98px"],
            fontWeight: "regular",
            textAlign: "center",
            mb: 1,
            mt: 5,
          }}
        >
          {productName}
        </h3>
        <p
          sx={{
            textAlign: "center",
            fontFamily: "body",
            fontSize: ["11px", "11px", "12px"],
            lineHeight: [1.45, 1.36, 1.33],
            fontWeight: "light",
            mb: 5,
          }}
        >
          {productDescription}
        </p>
        <p
          sx={{
            textAlign: "center",
            fontFamily: "body",
            fontSize: ["16px", "16px", "20px"],
            lineHeight: ["1.0", 1.0, 1.1075],
            fontWeight: "medium",
            mb: 1,
          }}
        >
          {displayCent(price)}
          {comparePrice && (
            <span
              sx={{
                fontWeight: "regular",
                textDecorationLine: "line-through",
                textDecorationThickness: "0.1em",
                fontSize: ["0.875em", "0.875em", "0.9em"],
                ml: 2,
              }}
            >
              {displayCent(comparePrice)}
            </span>
          )}
        </p>
        <Flex.row justify="center" align="center">
          {ourVariant.available !== false && ourVariant.inStock !== false && (
            <Touchable
              onClick={() => {
                cart.addSingle({
                  productID: productId,
                  variantID: singleVariant,
                })
                cart.show()
              }}
              title="Zum Warenkorb hinzufügen"
              sx={{
                fontFamily: "body",
                fontSize: ["14px", "14px", "15px"],
                lineHeight: [1.4, 1.288, 1.286],
                letterSpacing: ["1px", "0.67px", "0.98px"],
                textAlign: "center",
                textTransform: "uppercase",
                color: "white",
                pt: ["12.95px", "12.7516px", "12.57px"],
                pb: ["11.05px", "10.7516px", "10.57px"],
                px: ["14.7933px", "14.0017px", "13.61px"],
                bg: "almostBlack",
                mt: [5, 5, 6],
                mr: 2,
                mb: 5,
              }}
            >
              <RiIcon Component={FiShoppingCart} color="#ffffff" size={["14px", "14px", "15px"]} />
            </Touchable>
          )}
          {productPage && (
            <Touchable
              sx={{
                fontFamily: "body",
                fontSize: ["14px", "14px", "15px"],
                lineHeight: [1.4, 1.288, 1.286],
                letterSpacing: ["1px", "0.67px", "0.98px"],
                textAlign: "center",
                textTransform: "uppercase",
                color: "white",
                py: [4, 4, "11.81px"],
                px: [7, 7, "17.72px"],
                bg: "almostBlack",
                mt: [5, 5, 6],
                mb: 5,
              }}
              to={`/${productPage.slug.current}`}
            >
              Mehr erfahren
            </Touchable>
          )}
        </Flex.row>
      </div>
    </Flex.column>
  )
}

function ProdGridDesktop({ products, backgroundImage }) {
  return (
    <Grid columns={["unset", "repeat(2, 1fr)", "repeat(4, 1fr)"]} gap={["unset", "32px", "16px"]}>
      {products &&
        products.length > 0 &&
        products.length <= 4 &&
        products.map((product, index) => (
          <ProductCard key={index} product={product} backgroundImage={backgroundImage} />
        ))}
    </Grid>
  )
}

function ProdGridMobile({ products, backgroundImage }) {
  return (
    <div>
      <ProductCard product={products} backgroundImage={backgroundImage} />
    </div>
  )
}

function PrevSlideSelector({ changeSlide, numberOfArticles, current }) {
  const prev = current === 0 ? numberOfArticles - 1 : current - 1
  return (
    <button
      sx={{
        ...buttonStyles,
        boxShadow: "rgba(27, 45, 69, 0.1) 0px 5px 10px 0px",
        right: ["calc(-42.16vw + 157.85px)", "calc(-18.15vw + 138.20px)", "24px"],
      }}
      onClick={() => changeSlide(prev)}
    >
      <FiChevronLeft sx={{ fontSize: "25px" }} />
    </button>
  )
}

function NextSlideSelector({ changeSlide, numberOfArticles, current }) {
  const next = current === numberOfArticles - 1 ? 0 : current + 1
  return (
    <button
      sx={{
        ...buttonStyles,
        boxShadow: "rgba(27, 45, 69, 0.1) 0px 5px 10px 0px",
        left: ["calc(-42.16vw + 157.85px)", "calc(-18.15vw + 138.20px)", "24px"],
      }}
      onClick={() => changeSlide(next)}
    >
      <FiChevronRight sx={{ fontSize: "25px" }} />
    </button>
  )
}

function ProductSliderDesktop({ four, two, backgroundImage }) {
  const [slideIndex, setSlideIndex] = useState(0)
  const handleSlideToShow = index => {
    setSlideIndex(index)
  }
  const numberOfArticlesFour = four.length
  const numberOfArticlesTwo = two.length
  return (
    <Fragment>
      <div sx={{ display: ["none", "none", "block"] }}>
        <Carousel
          slideIndex={slideIndex}
          slidesToShow={1}
          enableKeyboardControls={true}
          dragging={true}
          wrapAround={true}
          afterSlide={index => setSlideIndex(index)}
          renderCenterLeftControls={() => (
            <PrevSlideSelector
              changeSlide={handleSlideToShow}
              numberOfArticles={numberOfArticlesFour}
              current={slideIndex}
            />
          )}
          renderBottomCenterControls={() => null}
          renderCenterRightControls={() => (
            <NextSlideSelector
              changeSlide={handleSlideToShow}
              numberOfArticles={numberOfArticlesFour}
              current={slideIndex}
            />
          )}
          renderBottomLeftControls={() => null}
          width="100%"
          slideWidth={1}
        >
          {four &&
            four.length > 0 &&
            four.map((subset, index) => (
              <ProdGridDesktop products={subset} key={index} backgroundImage={backgroundImage} />
            ))}
        </Carousel>
      </div>
      <div sx={{ display: ["none", "block", "none"] }}>
        <Carousel
          slideIndex={slideIndex}
          slidesToShow={1}
          enableKeyboardControls={true}
          dragging={true}
          wrapAround={true}
          afterSlide={index => setSlideIndex(index)}
          renderCenterLeftControls={() => (
            <PrevSlideSelector
              changeSlide={handleSlideToShow}
              numberOfArticles={numberOfArticlesTwo}
              current={slideIndex}
            />
          )}
          renderBottomCenterControls={() => null}
          renderCenterRightControls={() => (
            <NextSlideSelector
              changeSlide={handleSlideToShow}
              numberOfArticles={numberOfArticlesTwo}
              current={slideIndex}
            />
          )}
          renderBottomLeftControls={() => null}
          width="100%"
          slideWidth={1}
        >
          {two &&
            two.length > 0 &&
            two.map((subset, index) => (
              <ProdGridDesktop products={subset} key={index} backgroundImage={backgroundImage} />
            ))}
        </Carousel>
      </div>
    </Fragment>
  )
}

function ProductSliderMobile({ products, backgroundImage }) {
  const [slideIndex, setSlideIndex] = useState(0)
  const handleSlideToShow = index => {
    setSlideIndex(index)
  }
  const numberOfArticles = products.length
  return (
    <div sx={{ display: ["block", "none", "none"] }}>
      <Carousel
        slideIndex={slideIndex}
        slidesToShow={1}
        enableKeyboardControls={true}
        dragging={true}
        wrapAround={true}
        afterSlide={index => setSlideIndex(index)}
        renderCenterLeftControls={() => (
          <PrevSlideSelector
            changeSlide={handleSlideToShow}
            numberOfArticles={numberOfArticles}
            current={slideIndex}
          />
        )}
        renderBottomCenterControls={() => null}
        renderCenterRightControls={() => (
          <NextSlideSelector
            changeSlide={handleSlideToShow}
            numberOfArticles={numberOfArticles}
            current={slideIndex}
          />
        )}
        renderBottomLeftControls={() => null}
        width="100%"
        slideWidth={1}
      >
        {products &&
          products.length > 0 &&
          products.map((subset, index) => (
            <ProdGridMobile products={subset} key={index} backgroundImage={backgroundImage} />
          ))}
      </Carousel>
    </div>
  )
}

function FeaturedProducts({ title, titleAlignment, products, backgroundImage }) {
  const groupedProductsFour = products.reduce((acc, curr, index) => {
    const arr = [...acc]
    index % 4 === 0 ? arr.push([curr]) : arr[arr.length - 1].push(curr)
    return arr
  }, [])
  const groupedProductsTwo = products.reduce((acc, curr, index) => {
    const arr = [...acc]
    index % 2 === 0 ? arr.push([curr]) : arr[arr.length - 1].push(curr)
    return arr
  }, [])
  return (
    <SectionGrid sx={{ pt: [10, 13], pb: [7, 8], px: [4, 5, 0] }}>
      <SectionGrid.wide>
        <h2
          sx={{
            textAlign: titleAlignment,
            fontFamily: "heading",
            fontSize: ["28px", "28px", "38px"],
            lineHeight: ["30px", "30px", "44px"],
            fontWeight: ["medium", "medium", "regular"],
            letterSpacing: "-1.24px",
            mb: [2, 2, 5],
          }}
        >
          {title}
        </h2>
        <ProductSliderDesktop
          four={groupedProductsFour}
          two={groupedProductsTwo}
          backgroundImage={backgroundImage}
        />
        <ProductSliderMobile products={products} backgroundImage={backgroundImage} />
      </SectionGrid.wide>
    </SectionGrid>
  )
}

export { FeaturedProducts }
